import debounce from '../utilities/debounce';

export default function FilterableListing(el, {
    endpoint,
    source,
    resultsContHandle,
    refresh,
}) {
    const form = el.querySelector('[data-filter-form]');
    const resultsCont = el.querySelector(resultsContHandle) || el.querySelector('[data-results-container]');
    const topContentCont = el.querySelector('[data-top-content]');
    const drawer = el.querySelector('[data-filter-drawer]');
    const backdrop = el.querySelector('[data-filter-backdrop]');
    const openDrawerBtn = el.querySelector('[data-filter-open]');
    const closeDrawerBtn = el.querySelector('[data-filter-close]');
    const applyDrawerBtn = el.querySelector('[data-filter-apply]');
    const clearFiltersBtn = el.querySelector('[data-filter-clear]');
    const resultsCountEl = el.querySelector('[data-filter-results-count]');
    const showResultsCountEl = el.querySelector('[data-filter-show-count]');
    const chipsContainer = el.querySelector('[data-filter-chips]');
    const chipRemoveIconTpl = el.querySelector('[data-chip-remove-icon]');
    const chipRemoveIcon = chipRemoveIconTpl ? chipRemoveIconTpl.innerHTML.trim() : '×';
    const searchInput = form ? form.querySelector('input[type="search"]') : null;
    const sortSelects = el.querySelectorAll('[data-sort-select]');
    const sortLabels = el.querySelectorAll('[data-sort-label]');

    let activeRequest = null;
    let lastHistoryWasSearch = false;

    function syncSort(value) {
        if (!value) return;
        sortSelects.forEach(sel => {
            sel.value = value;
        });
        const firstSel = sortSelects[0];
        const selectedOpt = firstSel ? firstSel.querySelector(`option[value="${value}"]`) : null;
        const text = selectedOpt ? selectedOpt.textContent.trim() : value;
        sortLabels.forEach(lbl => {
            lbl.textContent = text;
        });
    }

    /* -------------------------------------------------------------------------
     * 1. Listing State & URL History
     * Every result set is reflected in the URL so it can be shared and revisited.
     * ----------------------------------------------------------------------- */
    function getStateParams(page = 1) {
        const params = new URLSearchParams();

        if (form) {
            Array.from(new FormData(form).entries()).forEach(([key, val]) => {
                if (val && String(val).trim() !== '') {
                    params.append(key, val);
                }
            });
        }

        const activeSort = sortSelects[0] ? sortSelects[0].value : 'relevance';
        if (activeSort && activeSort !== 'relevance') {
            params.set('sort', activeSort);
        }

        if (page > 1) {
            params.set('page', page);
        }

        return params;
    }

    function updateUrlHistory(params, history) {
        if (!window.history || history === 'none') return;

        const search = params.toString();
        const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        if (newUrl === currentUrl) return;

        if (history === 'replace') {
            window.history.replaceState({ path: newUrl }, '', newUrl);
        } else {
            window.history.pushState({ path: newUrl }, '', newUrl);
        }
    }

    /* -------------------------------------------------------------------------
     * 2. Active Chips Management
     * ----------------------------------------------------------------------- */
    function updateActiveChips() {
        if (!chipsContainer || !form) return;

        const chips = [];
        const checkedCheckboxes = form.querySelectorAll('input[type="checkbox"]:checked:not([data-compare-checkbox])');

        checkedCheckboxes.forEach(cb => {
            const labelEl = cb.closest('label');
            const labelText = labelEl ? labelEl.textContent.trim() : cb.value;
            chips.push({
                name: cb.name,
                value: cb.value,
                label: labelText,
                checkbox: cb,
            });
        });

        if (searchInput && searchInput.value.trim() !== '') {
            chips.push({
                name: 'q',
                value: searchInput.value.trim(),
                label: `"${searchInput.value.trim()}"`,
                isSearch: true,
            });
        }

        if (chips.length === 0) {
            chipsContainer.innerHTML = '';
            chipsContainer.style.display = 'none';

            return;
        }

        chipsContainer.style.display = 'flex';
        chipsContainer.innerHTML = `${chips.map((c, idx) => `
            <span class="filterable-listing__chip" data-chip-idx="${idx}">
                <span>${c.label}</span>
                <button type="button" aria-label="Remove filter ${c.label}" data-remove-chip="${idx}">${chipRemoveIcon}</button>
            </span>
        `).join('')}<button type="button" class="filterable-listing__chip-clear-all" data-chip-clear-all>Clear All</button>`;

        chipsContainer.querySelectorAll('[data-remove-chip]').forEach(btn => {
            btn.onclick = () => {
                const idx = parseInt(btn.getAttribute('data-remove-chip'), 10);
                const chip = chips[idx];
                if (!chip) return;

                if (chip.isSearch) {
                    if (searchInput) searchInput.value = '';
                    applyChange(); // eslint-disable-line no-use-before-define
                } else if (chip.checkbox) {
                    // The bubbling change event triggers the fetch via the form listener
                    chip.checkbox.checked = false;
                    chip.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                }
            };
        });

        const clearAllBtn = chipsContainer.querySelector('[data-chip-clear-all]');
        if (clearAllBtn) {
            clearAllBtn.onclick = () => {
                clearAllFilters(); // eslint-disable-line no-use-before-define
            };
        }
    }

    /* -------------------------------------------------------------------------
     * 3. Fetching Results via AJAX
     * The server decides what to render (results, top content) from the listing
     * source entry and the current params. Newer requests supersede older ones.
     *
     * @param {number} page    - Results page (filter changes reset to 1)
     * @param {string} history - 'push' | 'replace' | 'none'
     * ----------------------------------------------------------------------- */
    async function fetchResults({ page = 1, history = 'push' } = {}) {
        if (!endpoint || !source || !form || !resultsCont) return;

        if (activeRequest) {
            activeRequest.abort();
        }
        const request = new AbortController();
        activeRequest = request;

        resultsCont.classList.add('is-loading');

        const params = getStateParams(page);
        updateUrlHistory(params, history);

        const requestParams = new URLSearchParams(params);
        requestParams.set('source', source);

        try {
            const response = await fetch(`${endpoint}?${requestParams.toString()}`, {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: request.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            resultsCont.innerHTML = data.markup || '';

            if (topContentCont) {
                topContentCont.innerHTML = data.topContent || '';
            }

            const count = data.total || 0;
            if (resultsCountEl) {
                resultsCountEl.textContent = count;
            }
            if (showResultsCountEl) {
                showResultsCountEl.textContent = count;
            }

            updateActiveChips();

            if (refresh) {
                refresh(resultsCont);
                if (topContentCont) refresh(topContentCont);
            }

            el.dispatchEvent(new CustomEvent('listing:updated', {
                bubbles: true,
                detail: {
                    container: resultsCont,
                    total: count,
                },
            }));
        } catch (err) {
            // Superseded (aborted) or failed requests leave the current results in place
        } finally {
            if (activeRequest === request) {
                activeRequest = null;
                resultsCont.classList.remove('is-loading');
            }
        }
    }

    function clearAllFilters() {
        if (!form) return;

        form.querySelectorAll('input[type="checkbox"]:not([data-compare-checkbox])').forEach(cb => {
            cb.checked = false;
        });

        if (searchInput) {
            searchInput.value = '';
        }

        applyChange(); // eslint-disable-line no-use-before-define
    }

    function expandActiveFilterGroups() {
        el.querySelectorAll('[data-filter-accordion]').forEach(details => {
            if (details.querySelector('input[type="checkbox"]:checked')) {
                details.open = true;
            }
        });
    }

    /* -------------------------------------------------------------------------
     * 4. Mobile Drawer Controls
     * ----------------------------------------------------------------------- */
    function openDrawer() {
        if (!drawer) return;
        drawer.classList.add('is-open');
        if (backdrop) backdrop.style.display = 'block';
        document.body.classList.add('is-locked');
    }

    function closeDrawer() {
        if (!drawer) return;
        drawer.classList.remove('is-open');
        if (backdrop) backdrop.style.display = 'none';
        document.body.classList.remove('is-locked');
    }

    if (openDrawerBtn) openDrawerBtn.onclick = openDrawer;
    if (closeDrawerBtn) closeDrawerBtn.onclick = closeDrawer;
    if (backdrop) backdrop.onclick = closeDrawer;
    if (applyDrawerBtn) applyDrawerBtn.onclick = closeDrawer;
    if (clearFiltersBtn) clearFiltersBtn.onclick = clearAllFilters;

    // Each filter change adds a history entry; consecutive search keystrokes share one
    function applyChange() {
        lastHistoryWasSearch = false;
        fetchResults();
    }

    if (form) {
        form.onsubmit = e => {
            e.preventDefault();
            applyChange();
        };

        form.addEventListener('change', e => {
            if (e.target === searchInput) return; // handled by the input listener
            if (e.target.hasAttribute('data-compare-checkbox')) return;
            applyChange();
        });

        if (searchInput) {
            searchInput.addEventListener('input', debounce(() => {
                fetchResults({ history: lastHistoryWasSearch ? 'replace' : 'push' });
                lastHistoryWasSearch = true;
            }, 350));
        }
    }

    sortSelects.forEach(sel => {
        sel.addEventListener('change', () => {
            syncSort(sel.value);
            applyChange();
        });
    });

    // Pagination links are relative hrefs carrying `data-page`; load them in place
    if (resultsCont) {
        resultsCont.addEventListener('click', e => {
            const link = e.target.closest('a[data-page]');
            if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

            e.preventDefault();
            lastHistoryWasSearch = false;
            fetchResults({ page: parseInt(link.dataset.page, 10) || 1 });
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    // Browser Back / Forward: restore the form from the URL, re-fetch without touching history
    window.addEventListener('popstate', () => {
        if (!form) return;
        const url = new URL(window.location.href);

        form.querySelectorAll('input[type="checkbox"]:not([data-compare-checkbox])').forEach(cb => {
            const vals = url.searchParams.getAll(cb.name);
            cb.checked = vals.includes(cb.value);
        });

        if (searchInput) {
            searchInput.value = url.searchParams.get('q') || '';
        }

        syncSort(url.searchParams.get('sort') || 'relevance');

        lastHistoryWasSearch = false;
        fetchResults({
            page: parseInt(url.searchParams.get('page'), 10) || 1,
            history: 'none',
        });
    });

    // Initialize state
    const initialUrl = new URL(window.location.href);
    const initialSort = initialUrl.searchParams.get('sort');
    if (initialSort) {
        syncSort(initialSort);
    } else if (sortSelects[0]) {
        syncSort(sortSelects[0].value);
    }

    expandActiveFilterGroups();
    updateActiveChips();
}
